/**
 * Section B8 Page - B.8.監測計畫
 * Monitoring Plan
 * UI 模式：4 頁籤
 *   B.8.1 DataTable + Modal (14 欄位)
 *   B.8.2 抽樣計畫（富文本）
 *   B.8.3 監測管理系統（富文本）
 *   B.8.4 計入期後監測計畫（富文本，選填）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import LatexHelper from '../components/LatexHelper';
import sectionB8Service, {
  SectionB8Detail, ParameterRow, ParameterRowCreate, B74AvailableParam,
} from '../services/sectionB8Service';
import '../styles/DataTable.css';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

// ── 常用單位 ──────────────────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e',     latex: '\\text{tCO}_2\\text{e}' },
  { label: 'ktCO₂e',    latex: '\\text{ktCO}_2\\text{e}' },
  { label: 'MtCO₂e',    latex: '\\text{MtCO}_2\\text{e}' },
  { label: 'MWh',        latex: '\\text{MWh}' },
  { label: 'GWh',        latex: '\\text{GWh}' },
  { label: 'kWh',        latex: '\\text{kWh}' },
  { label: 'GJ',         latex: '\\text{GJ}' },
  { label: 'MJ',         latex: '\\text{MJ}' },
  { label: 'kg',         latex: '\\text{kg}' },
  { label: 't',          latex: '\\text{t}' },
  { label: 'm³',         latex: '\\text{m}^3' },
  { label: 'Nm³',        latex: '\\text{Nm}^3' },
  { label: 'L',          latex: '\\text{L}' },
  { label: '%',          latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ',  latex: '\\text{tCO}_2\\text{e/GJ}' },
];

// ── UnitInput ─────────────────────────────────────────────────────
interface UnitInputProps { value: string; onChange: (v: string) => void; disabled?: boolean; }
const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const preview = () => {
    if (!value.trim()) return null;
    try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          placeholder="LaTeX，如 \text{tCO}_2\text{e}"
          style={{ flex: 1, padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 12 }} />
        {value.trim() && (
          <div style={{ minWidth: 50, padding: '2px 6px', background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {preview()}
          </div>
        )}
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)}
            style={{ padding: '4px 7px', fontSize: 13, borderRadius: 5, border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa', color: showPicker ? '#fff' : '#555', cursor: 'pointer' }}>≡</button>
        )}
      </div>
      {showPicker && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 300, marginTop: 4, background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 300 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button" onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #e0e0e0', background: '#fafafa', cursor: 'pointer' }}>
                {u.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SafeInlineMath ────────────────────────────────────────────────
function SafeInlineMath({ math }: { math: string }) {
  if (!math.trim()) return null;
  try { return <InlineMath math={math} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
}

// ── SafeBlockMath ─────────────────────────────────────────────────
function SafeBlockMath({ math }: { math: string }) {
  if (!math.trim()) return null;
  try { return <BlockMath math={math} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
}

// ── SourceTag ─────────────────────────────────────────────────────
function SourceTag({ b74Ref }: { b74Ref: ParameterRow['b74_ref'] }) {
  if (b74Ref) {
    return (
      <span style={{ padding: '1px 7px', fontSize: 11, borderRadius: 10,
        background: '#e6f4ff', border: '1px solid #91caff', color: '#0958d9', fontWeight: 500, whiteSpace: 'nowrap' }}>
        B.7.4
      </span>
    );
  }
  return (
    <span style={{ padding: '1px 7px', fontSize: 11, borderRadius: 10,
      background: '#f5f5f5', border: '1px solid #d9d9d9', color: '#888', fontWeight: 500 }}>
      手動
    </span>
  );
}

// ── UUID ──────────────────────────────────────────────────────────
function genId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── Purpose Tags ──────────────────────────────────────────────────
function PurposeTags({ row, t }: { row: ParameterRow; t: (k: string) => string }) {
  const tags = [];
  if (row.purpose_baseline) tags.push({ key: 'b', label: t('sectionB8.purposeTagBaseline'), bg: '#e6f4ff', bd: '#91caff', color: '#0958d9' });
  if (row.purpose_project)  tags.push({ key: 'p', label: t('sectionB8.purposeTagProject'),  bg: '#f6ffed', bd: '#b7eb8f', color: '#389e0d' });
  if (row.purpose_leakage)  tags.push({ key: 'l', label: t('sectionB8.purposeTagLeakage'),  bg: '#fff7e6', bd: '#ffd591', color: '#d46b08' });
  if (!tags.length) return <span style={{ color: '#aaa', fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {tags.map(tag => (
        <span key={tag.key} style={{ padding: '1px 7px', fontSize: 11, borderRadius: 10, background: tag.bg, border: `1px solid ${tag.bd}`, color: tag.color, fontWeight: 500 }}>
          {tag.label}
        </span>
      ))}
    </div>
  );
}

// ── 空白 ParameterRow ─────────────────────────────────────────────
function emptyRow(): ParameterRowCreate {
  return {
    data_parameter: '', description: '', data_unit: '', equations_referred: '',
    purpose_baseline: false, purpose_project: false, purpose_leakage: false,
    measurement_methods: null, entity_responsible: '', instrument_type: '',
    instrument_accuracy: '', instrument_calibration: null, instrument_location: '',
    measurement_intervals: null, qa_qc_procedures: null, additional_comment: null,
    b74_ref: null,
  };
}

// ════════════════════════════════════════════════════════════════
// ParameterModal
// ════════════════════════════════════════════════════════════════
interface ModalProps {
  projectId: number;
  entry: ParameterRow | null;
  canEdit: boolean;
  isZh: boolean;
  availableB74: B74AvailableParam[];
  onClose: () => void;
  onSaved: (d: SectionB8Detail, continueAdding: boolean) => void;
}

const ParameterModal: React.FC<ModalProps> = ({ projectId, entry, canEdit, isZh, availableB74, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const isNew = entry === null;

  const [form, setForm] = useState<ParameterRowCreate>(isNew ? emptyRow() : {
    data_parameter:         entry!.data_parameter,
    description:            entry!.description,
    data_unit:              entry!.data_unit,
    equations_referred:     entry!.equations_referred,
    purpose_baseline:       entry!.purpose_baseline,
    purpose_project:        entry!.purpose_project,
    purpose_leakage:        entry!.purpose_leakage,
    measurement_methods:    entry!.measurement_methods,
    entity_responsible:     entry!.entity_responsible,
    instrument_type:        entry!.instrument_type,
    instrument_accuracy:    entry!.instrument_accuracy,
    instrument_calibration: entry!.instrument_calibration,
    instrument_location:    entry!.instrument_location,
    measurement_intervals:  entry!.measurement_intervals,
    qa_qc_procedures:       entry!.qa_qc_procedures,
    additional_comment:     entry!.additional_comment,
    b74_ref:                entry!.b74_ref ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [selectedB74Sym, setSelectedB74Sym] = useState<string>('');

  // LaTeX 輔助插入（data_parameter）
  const [paramRef, setParamRef] = useState<HTMLInputElement | null>(null);
  const insertLatexToParam = (snippet: string) => {
    if (!paramRef) { set('data_parameter', form.data_parameter + snippet); return; }
    const s = paramRef.selectionStart ?? paramRef.value.length;
    const e = paramRef.selectionEnd ?? paramRef.value.length;
    const next = paramRef.value.slice(0, s) + snippet + paramRef.value.slice(e);
    set('data_parameter', next);
    setTimeout(() => { paramRef.focus(); paramRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  // LaTeX 輔助插入（equations_referred）
  const [eqRef, setEqRef] = useState<HTMLInputElement | null>(null);
  const insertLatexToEq = (snippet: string) => {
    if (!eqRef) { set('equations_referred', form.equations_referred + snippet); return; }
    const s = eqRef.selectionStart ?? eqRef.value.length;
    const e = eqRef.selectionEnd ?? eqRef.value.length;
    const next = eqRef.value.slice(0, s) + snippet + eqRef.value.slice(e);
    set('equations_referred', next);
    setTimeout(() => { eqRef.focus(); eqRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  const set = (k: keyof ParameterRowCreate, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSelectB74 = (sym: string) => {
    setSelectedB74Sym(sym);
    if (!sym) {
      set('b74_ref', null);
      return;
    }
    const param = availableB74.find(p => p.symbol === sym);
    if (param) {
      set('data_parameter', param.symbol);
      set('data_unit', param.unit || '');
      set('b74_ref', { symbol: param.symbol, para_type: param.para_type, equation_name: param.equation_name, equation: param.equation });
    }
  };

  const handleSave = async (continueAdding = false) => {
    setSaving(true);
    try {
      let res: SectionB8Detail;
      if (isNew) {
        res = await sectionB8Service.addParameter(projectId, form);
      } else {
        res = await sectionB8Service.updateParameter(projectId, entry!.id, form);
      }
      show(true, t('sectionB8.saveSuccess'));
      onSaved(res, continueAdding);
      if (!continueAdding) onClose();
      else { setForm(emptyRow()); setSelectedB74Sym(''); }
    } catch {
      show(false, t('sectionB8.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = { width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as const };
  const labelStyle = { fontSize: 13, fontWeight: 600 as const, display: 'block' as const, marginBottom: 4 };
  const sectionHeader = (txt: string) => (
    <div style={{ fontWeight: 700, fontSize: 13, color: '#16a085', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 12, marginTop: 4 }}>{txt}</div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 1100, maxWidth: '96vw', height: '90vh', background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: 'linear-gradient(135deg, #16a085 0%, #0e6655 100%)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>
            {isNew ? t('sectionB8.modalNewTitle') : t('sectionB8.modalEditTitle')}
            {form.data_parameter.trim() && (
              <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 8, opacity: 0.85 }}>— <SafeInlineMath math={form.data_parameter} /></span>
            )}
          </div>
          <button onClick={onClose} style={{ fontSize: 20, background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: '#fff', lineHeight: 1, borderRadius: 6, padding: '2px 8px' }}>✕</button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* B.7.4 來源選擇（新增模式） */}
          {isNew && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae0ff', borderRadius: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                {isZh ? '從 B.7.4 帶入監測參數（選填）' : 'Import from B.7.4 monitoring parameter (optional)'}
              </label>
              <select value={selectedB74Sym} onChange={e => handleSelectB74(e.target.value)} disabled={!canEdit}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}>
                <option value="">{isZh ? '— 手動輸入（不選擇）—' : '— Enter manually (no selection) —'}</option>
                {availableB74.map(p => (
                  <option key={p.symbol} value={p.symbol}>
                    {p.symbol}{p.equation_name ? ` — ${p.equation_name}` : ''}{p.para_type === null ? ' (皆適用)' : ' (事後監測)'}
                  </option>
                ))}
              </select>
              {form.b74_ref?.equation && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#fff', border: '1px solid #d0e8ff', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{form.b74_ref.equation_name}</div>
                  <SafeBlockMath math={form.b74_ref.equation} />
                </div>
              )}
            </div>
          )}
          {/* 已有 b74_ref 的編輯模式顯示來源 */}
          {!isNew && entry?.b74_ref && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae0ff', borderRadius: 6, fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: '#0958d9' }}>來源 B.7.4：</span>
              {entry.b74_ref.equation_name && <span style={{ marginLeft: 6 }}>{entry.b74_ref.equation_name}</span>}
              {entry.b74_ref.equation && <div style={{ marginTop: 4 }}><SafeBlockMath math={entry.b74_ref.equation} /></div>}
            </div>
          )}

          {/* 基本識別 */}
          {sectionHeader(`${isZh ? '基本識別' : 'Identification'}`)}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

            {/* data_parameter — LaTeX 輸入 + LatexHelper + 預覽 */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB8.fieldDataParameter')}</label>
                <LatexHelper isZh={isZh} onInsert={insertLatexToParam} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input ref={setParamRef} value={form.data_parameter} onChange={e => set('data_parameter', e.target.value)} disabled={!canEdit}
                  placeholder={t('sectionB8.fieldDataParameterPh')}
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }} />
                {form.data_parameter.trim() && (
                  <div style={{ minWidth: 60, padding: '4px 10px', background: '#f5f8ff', border: '1px solid #d0e4ff', borderRadius: 6, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SafeInlineMath math={form.data_parameter} />
                  </div>
                )}
              </div>
            </div>

            {/* data_unit — UnitInput（LaTeX + 常用單位選擇器）*/}
            <div>
              <label style={labelStyle}>{t('sectionB8.fieldDataUnit')}</label>
              <UnitInput value={form.data_unit} onChange={v => set('data_unit', v)} disabled={!canEdit} />
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>{t('sectionB8.fieldDescription')}</label>
              <input value={form.description} onChange={e => set('description', e.target.value)} disabled={!canEdit} style={fieldStyle} placeholder={t('sectionB8.fieldDescriptionPh')} />
            </div>

            {/* equations_referred — LaTeX 輸入 + LatexHelper */}
            <div style={{ gridColumn: '1/-1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB8.fieldEquationsReferred')}</label>
                <LatexHelper isZh={isZh} onInsert={insertLatexToEq} />
              </div>
              <input ref={setEqRef} value={form.equations_referred} onChange={e => set('equations_referred', e.target.value)} disabled={!canEdit}
                placeholder={t('sectionB8.fieldEquationsReferredPh')}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }} />
            </div>

          </div>

          {/* 資料用途 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t('sectionB8.fieldPurpose')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {([
                ['purpose_baseline', t('sectionB8.purposeBaseline')],
                ['purpose_project',  t('sectionB8.purposeProject')],
                ['purpose_leakage',  t('sectionB8.purposeLeakage')],
              ] as [keyof ParameterRowCreate, string][]).map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={!!form[key]} onChange={e => canEdit && set(key, e.target.checked)} disabled={!canEdit} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* 量測方法 */}
          {sectionHeader(`${isZh ? '量測方法與程序' : 'Measurement Methods & Procedures'}`)}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t('sectionB8.fieldMeasurementMethods')}</label>
            <RichTextEditor value={form.measurement_methods || ''} onChange={v => set('measurement_methods', v || null)} disabled={!canEdit} minHeight="80px" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t('sectionB8.fieldEntityResponsible')}</label>
            <input value={form.entity_responsible} onChange={e => set('entity_responsible', e.target.value)} disabled={!canEdit} style={fieldStyle} placeholder={t('sectionB8.fieldEntityResponsiblePh')} />
          </div>

          {/* 量測儀器 */}
          {sectionHeader(`${isZh ? '量測儀器' : 'Measuring Instrument'}`)}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>{t('sectionB8.fieldInstrumentType')}</label>
              <input value={form.instrument_type} onChange={e => set('instrument_type', e.target.value)} disabled={!canEdit} style={fieldStyle} placeholder={t('sectionB8.fieldInstrumentTypePh')} />
            </div>
            <div>
              <label style={labelStyle}>{t('sectionB8.fieldInstrumentAccuracy')}</label>
              <input value={form.instrument_accuracy} onChange={e => set('instrument_accuracy', e.target.value)} disabled={!canEdit} style={fieldStyle} placeholder={t('sectionB8.fieldInstrumentAccuracyPh')} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>{t('sectionB8.fieldInstrumentLocation')}</label>
              <input value={form.instrument_location} onChange={e => set('instrument_location', e.target.value)} disabled={!canEdit} style={fieldStyle} placeholder={t('sectionB8.fieldInstrumentLocationPh')} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t('sectionB8.fieldInstrumentCalibration')}</label>
            <RichTextEditor value={form.instrument_calibration || ''} onChange={v => set('instrument_calibration', v || null)} disabled={!canEdit} minHeight="80px" />
          </div>

          {/* 量測頻率 & QA/QC */}
          {sectionHeader(`${isZh ? '量測頻率與品質管控' : 'Measurement Intervals & QA/QC'}`)}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t('sectionB8.fieldMeasurementIntervals')}</label>
            <RichTextEditor value={form.measurement_intervals || ''} onChange={v => set('measurement_intervals', v || null)} disabled={!canEdit} minHeight="80px" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t('sectionB8.fieldQaQc')}</label>
            <RichTextEditor value={form.qa_qc_procedures || ''} onChange={v => set('qa_qc_procedures', v || null)} disabled={!canEdit} minHeight="80px" />
          </div>

          {/* 備註 */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>
              {t('sectionB8.fieldAdditionalComment')} <span style={{ color: '#aaa', fontWeight: 400 }}>({t('sectionB8.optional')})</span>
            </label>
            <RichTextEditor value={form.additional_comment || ''} onChange={v => set('additional_comment', v || null)} disabled={!canEdit} minHeight="60px" />
          </div>

          {/* Save */}
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => handleSave(false)} disabled={saving}
                style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#16a085', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {saving ? t('sectionB8.saving') : t('sectionB8.save')}
              </button>
              {isNew && (
                <button onClick={() => handleSave(true)} disabled={saving}
                  style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: '1px solid #16a085', background: '#fff', color: '#16a085', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                  {isZh ? '儲存並繼續新增' : 'Save & Add Another'}
                </button>
              )}
              {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════════════════
// TextTab：B.8.2 / B.8.3 / B.8.4 富文本頁籤
// ════════════════════════════════════════════════════════════════
interface TextTabProps {
  projectId: number;
  fieldKey: 'sampling_plan' | 'monitoring_mgmt' | 'post_crediting_plan';
  initialValue: string | null;
  canEdit: boolean;
  isOptional?: boolean;
  hint?: string;
  onSaved: (d: SectionB8Detail) => void;
}

const TextTab: React.FC<TextTabProps> = ({ projectId, fieldKey, initialValue, canEdit, isOptional, hint, onSaved }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [value, setValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(initialValue || ''); }, [initialValue]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB8Service.updateTextFields(projectId, { [fieldKey]: value });
      show(true, t('sectionB8.saveSuccess'));
      onSaved(res);
    } catch {
      show(false, t('sectionB8.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      {hint && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#e6f7ff', border: '1px solid #91caff', borderRadius: 6, fontSize: 13, color: '#0958d9' }}>
          ℹ {hint}
        </div>
      )}
      <RichTextEditor value={value} onChange={setValue} disabled={!canEdit} minHeight="200px" />
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#16a085', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {saving ? t('sectionB8.saving') : t('sectionB8.save')}
          </button>
          {isOptional && <span style={{ fontSize: 12, color: '#aaa' }}>({t('sectionB8.optional')})</span>}
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};


// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
const SectionB8Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canUpdate = hasPermission('section_b8', 'update');

  const [detail, setDetail] = useState<SectionB8Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'b81' | 'b82' | 'b83' | 'b84'>('b81');
  const [modalEntry, setModalEntry] = useState<ParameterRow | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [availableB74, setAvailableB74] = useState<B74AvailableParam[]>([]);
  const [b74Built, setB74Built] = useState(0);
  const [b74Total, setB74Total] = useState(0);

  const entries: ParameterRow[] = detail?.parameters ?? [];

  // ── Load ──────────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionB8Page');
      const [res, avail] = await Promise.all([
        sectionB8Service.getSectionB8ByProject(projectId),
        sectionB8Service.getB74AvailableParams(projectId).catch(() => ({ available: [], built: 0, total: 0 })),
      ]);
      setDetail(res);
      setAvailableB74(avail.available);
      setB74Built(avail.built);
      setB74Total(avail.total);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); setModalEntry(undefined); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = async (entryId: string) => {
    if (!selectedProjectId) return;
    try {
      await sectionB8Service.deleteParameter(selectedProjectId, entryId);
      loadData(selectedProjectId);
    } catch { toast.error(t('sectionB8.deleteFailed')); }
    setDeleteTarget(null);
  };

  // ── Reset ─────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionB8Service.resetSectionB8(selectedProjectId);
      setDetail(null);
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── handleSaved ───────────────────────────────────────────────
  const handleSaved = async (d: SectionB8Detail, continueAdding: boolean) => {
    setDetail(d);
    if (selectedProjectId) {
      try {
        const avail = await sectionB8Service.getB74AvailableParams(selectedProjectId);
        setAvailableB74(avail.available);
        setB74Built(avail.built);
        setB74Total(avail.total);
      } catch { /* ignore */ }
    }
    if (!continueAdding) setModalEntry(undefined);
  };

  // ── Tab style ─────────────────────────────────────────────────
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '9px 20px', fontSize: 13, fontWeight: active ? 700 : 400,
    borderBottom: active ? '3px solid #16a085' : '3px solid transparent',
    color: active ? '#16a085' : '#555', cursor: 'pointer', background: 'none', border: 'none',
    borderBottomStyle: 'solid', borderBottomWidth: 3,
    borderBottomColor: active ? '#16a085' : 'transparent',
    transition: 'all 0.15s',
  });

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b8" />
        {canUpdate && detail && (
          <button onClick={() => setResetConfirm(true)}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
            🗑 {t('sectionB8.resetData')}
          </button>
        )}
      </div>

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>{t('sectionB8.selectProject')}</div>
      ) : loading ? (
        <div className="loading">{t('sectionB8.loading')}</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', padding: '0 20px', gap: 4 }}>
            <button style={tabStyle(activeTab === 'b81')} onClick={() => setActiveTab('b81')}>{t('sectionB8.tab81')}</button>
            <button style={tabStyle(activeTab === 'b82')} onClick={() => setActiveTab('b82')}>{t('sectionB8.tab82')}</button>
            <button style={tabStyle(activeTab === 'b83')} onClick={() => setActiveTab('b83')}>{t('sectionB8.tab83')}</button>
            <button style={tabStyle(activeTab === 'b84')} onClick={() => setActiveTab('b84')}>{t('sectionB8.tab84')}</button>
          </div>

          <div style={{ padding: '20px 24px' }}>

            {/* ── B.8.1 ── */}
            {activeTab === 'b81' && (
              <div>
                {/* B.7.4 必要參數進度列 */}
                {b74Total > 0 && (
                  <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 8,
                    background: b74Built >= b74Total ? '#f6ffed' : '#fffbe6',
                    border: `1px solid ${b74Built >= b74Total ? '#b7eb8f' : '#ffe58f'}` }}>
                    <span style={{ fontSize: 13, fontWeight: 600,
                      color: b74Built >= b74Total ? '#389e0d' : '#d46b08' }}>
                      {isZh ? `已建立 ${b74Built} / 共 ${b74Total} 個必要監測參數` : `Built ${b74Built} / ${b74Total} required monitoring parameters`}
                    </span>
                    {b74Built >= b74Total && <span style={{ marginLeft: 8, fontSize: 12 }}>✓ 已完成</span>}
                  </div>
                )}
                {canUpdate && (
                  <div style={{ marginBottom: 14 }}>
                    <button onClick={() => setModalEntry(null)}
                      style={{ padding: '7px 18px', fontSize: 13, borderRadius: 6, border: '1px solid #16a085', background: '#16a085', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                      + {t('sectionB8.addParameter')}
                    </button>
                  </div>
                )}
                {entries.length === 0 ? (
                  <div style={{ color: '#aaa', fontSize: 14, padding: '24px 0' }}>{t('sectionB8.noData')}</div>
                ) : (
                  <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead className="table-header-dark-green">
                      <tr>
                        <th style={{ width: 44, textAlign: 'center' }}>#</th>
                        <th>{t('sectionB8.colParameter')}</th>
                        <th>{t('sectionB8.colDescription')}</th>
                        <th>{t('sectionB8.colUnit')}</th>
                        <th>{t('sectionB8.colPurpose')}</th>
                        <th style={{ width: 60, textAlign: 'center' }}>{isZh ? '來源' : 'Source'}</th>
                        <th style={{ width: 160, textAlign: 'center' }}>{t('sectionB8.colActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((row, idx) => (
                        <tr key={row.id}>
                          <td style={{ textAlign: 'center', color: '#888' }}>{idx + 1}</td>
                          <td style={{ fontWeight: 500 }}>{row.data_parameter ? <SafeInlineMath math={row.data_parameter} /> : <span style={{ color: '#aaa' }}>—</span>}</td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description}>{row.description || <span style={{ color: '#aaa' }}>—</span>}</td>
                          <td>{row.data_unit ? <SafeInlineMath math={row.data_unit} /> : <span style={{ color: '#aaa' }}>—</span>}</td>
                          <td><PurposeTags row={row} t={t} /></td>
                          <td style={{ textAlign: 'center' }}><SourceTag b74Ref={row.b74_ref} /></td>
                          <td style={{ textAlign: 'center' }}>
                            <button onClick={() => setModalEntry(row)}
                              style={{ padding: '3px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#fff', color: '#4A90E2', cursor: 'pointer', marginRight: 6 }}>
                              {t('sectionB8.edit')}
                            </button>
                            {canUpdate && (
                              <button onClick={() => setDeleteTarget(row.id)}
                                style={{ padding: '3px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #ff4d4f', background: '#fff', color: '#ff4d4f', cursor: 'pointer' }}>
                                {t('sectionB8.delete')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── B.8.2 ── */}
            {activeTab === 'b82' && (
              <TextTab
                projectId={selectedProjectId!}
                fieldKey="sampling_plan"
                initialValue={detail?.sampling_plan ?? null}
                canEdit={canUpdate}
                onSaved={setDetail}
              />
            )}

            {/* ── B.8.3 ── */}
            {activeTab === 'b83' && (
              <TextTab
                projectId={selectedProjectId!}
                fieldKey="monitoring_mgmt"
                initialValue={detail?.monitoring_mgmt ?? null}
                canEdit={canUpdate}
                onSaved={setDetail}
              />
            )}

            {/* ── B.8.4 ── */}
            {activeTab === 'b84' && (
              <TextTab
                projectId={selectedProjectId!}
                fieldKey="post_crediting_plan"
                initialValue={detail?.post_crediting_plan ?? null}
                canEdit={canUpdate}
                isOptional
                hint={isZh
                  ? '本節僅適用於涉及移除量 (removals) 的專案，為選填項目。'
                  : 'This section applies only to projects involving removals. It is optional.'}
                onSaved={setDetail}
              />
            )}
          </div>
        </div>
      )}

      {/* Parameter Modal */}
      {modalEntry !== undefined && selectedProjectId && (
        <ParameterModal
          projectId={selectedProjectId}
          entry={modalEntry}
          canEdit={canUpdate}
          isZh={isZh}
          availableB74={availableB74}
          onClose={() => setModalEntry(undefined)}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 320, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, marginBottom: 20 }}>{t('sectionB8.deleteConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteTarget(null)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('sectionB8.cancel')}</button>
              <button onClick={() => handleDelete(deleteTarget)} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('sectionB8.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionB8.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionB8.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('sectionB8.cancel')}</button>
              <button onClick={handleReset} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('sectionB8.reset')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionB8Page;
